import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiTrash2 } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";

import { cn } from "utils/helpers";
import type { CompactSelectOption } from "newui/components/ui/InputWithLabel";

import type { ColumnConfig } from "../../SpreadsheetEditor";
import type { ScopeItemRowDraft } from "../ScopeItemRowsEditor";
import { TemplateRowCell } from "./TemplateRowCell";

const STICKY_CELL_STYLE: React.CSSProperties = {
	position: "sticky",
	left: 0,
	zIndex: 5,
	background: "white",
};

const CELL_BASE = "border border-[#EAECF0] px-1 py-1 text-sm";

export interface TemplateRowProps {
	row: ScopeItemRowDraft;
	rowIndex: number;
	columns: ColumnConfig[];
	nameColumnId: string | null;
	unitColumnId: string | null;
	unitOptions: CompactSelectOption[];
	/** True when this row violates the mandatory-UoM rule. */
	unitHasError?: boolean;
	onChangeRow: (
		rowId: number,
		field: "name" | "unitId",
		value: string | number | null
	) => void;
	onRemoveRow: (rowId: number) => void;
}

/**
 * A single sortable scope-item row. Renders the leading sticky #/drag-handle
 * cell, one TemplateRowCell per column, and a trailing delete-button cell.
 */
export const TemplateRow: React.FC<TemplateRowProps> = ({
	row,
	rowIndex,
	columns,
	nameColumnId,
	unitColumnId,
	unitOptions,
	unitHasError = false,
	onChangeRow,
	onRemoveRow,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: String(row.id) });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<tr
			ref={setNodeRef}
			role="row"
			style={style}
			data-testid={`template-row-${row.id}`}
		>
			<td
				role="gridcell"
				style={STICKY_CELL_STYLE}
				className={cn(CELL_BASE, "w-14 bg-gray-50")}
			>
				<div className="flex items-center gap-1">
					<button
						type="button"
						className="cursor-grab text-tertiary-600 hover:text-primary-500 active:cursor-grabbing"
						aria-label={`Drag row ${rowIndex + 1}`}
						data-testid={`drag-row-${row.id}`}
						{...attributes}
						{...listeners}
					>
						<MdDragIndicator size={14} />
					</button>
					<span className="font-roboto text-xs text-primary-500">
						{rowIndex + 1}
					</span>
				</div>
			</td>
			{columns.map((column) => (
				<TemplateRowCell
					key={column.localId}
					column={column}
					row={row}
					rowIndex={rowIndex}
					nameColumnId={nameColumnId}
					unitColumnId={unitColumnId}
					unitOptions={unitOptions}
					unitHasError={unitHasError}
					onChangeRow={onChangeRow}
				/>
			))}
			<td role="gridcell" className="w-10 border-0 px-1">
				<button
					type="button"
					className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
					onClick={() => onRemoveRow(row.id)}
					title="Remove row"
					aria-label={`Remove row ${rowIndex + 1}`}
					data-testid={`remove-template-row-${row.id}`}
				>
					<FiTrash2 size={12} />
				</button>
			</td>
		</tr>
	);
};

export default TemplateRow;
